import React from 'react';
import { useSelector } from 'react-redux';

import classnames from 'clsx';
import Check from 'mdi-material-ui/Check';
import { Checkbox, FormControlLabel } from '@material-ui/core';

import { FieldsEnum, FormsEnum, InstallmentsEnum } from 'app/shared/enums';
import { Image } from 'app/components/Image';
import { NewButton } from 'app/components/NewButton';
import { ApplicationInfoProps } from './ApplicationInfo.interfaces';
import { useStyles } from './ApplicationInfo.styles';
import useApplicationInfo from './useApplicationInfo';
import { RootState } from 'app/store/rootReducer';
import { selectQuestion } from 'app/store/data/formsConfig';
import { selectIsFormMicroCenterActive } from 'app/store/selectors/selectIsFormMicroCenterActive';

const ApplicationInfo = (props: ApplicationInfoProps) => {
  const classes = useStyles();

  const {
    handleHiproConsentClick,
    handleActionButtonClick,
    isLoading,
    hiProConsentError,
    hiProConsentChecked,
  } = useApplicationInfo();

  const hiProConsent = useSelector((state: RootState) =>
    selectQuestion({
      state,
      formName: FormsEnum.CheckoutPaymentForm,
      questionKey: FieldsEnum.HiProConsent,
    })
  );

  const isFormMicroCenterActive = useSelector(selectIsFormMicroCenterActive);

  return (
    <div className={classes.modalContent}>
      <div className={classes.header}>
        <div className={classes.heading}>
          <h4>{InstallmentsEnum.OpenApplication}</h4>
        </div>
      </div>
      <div>
        <div className={classes.bulletpoint}>
          <Check className={classes.icon} />
          <p>
            {InstallmentsEnum.ModalBulletpoint1}
            <strong>{InstallmentsEnum.ModalBulletpoint1Bold}</strong>
          </p>
        </div>
        <div className={classes.bulletpoint}>
          <Check className={classes.icon} />
          <p>
            {InstallmentsEnum.ModalBulletpoint2}
            <strong>{InstallmentsEnum.ModalBulletpoint2Bold}</strong>
          </p>
        </div>
        <div className={classes.bulletpoint}>
          <Check className={classes.icon} />
          <p>
            {InstallmentsEnum.ModalBulletpoint3}
            <strong>{InstallmentsEnum.ModalBulletpoint3Bold}</strong>
          </p>
        </div>
        <div className={classes.bulletpoint}>
          <Check className={classes.icon} />
          <p>{InstallmentsEnum.ModalBulletpoint4}</p>
        </div>
        <p className={classes.description}>
          {InstallmentsEnum.GetReadyDocuments}
        </p>
      </div>
      <FormControlLabel
        tabIndex={0}
        checked={hiProConsentChecked}
        control={
          <Checkbox
            tabIndex={-1}
            name={'hipro-consent'}
            onClick={handleHiproConsentClick}
            color="primary"
            data-cy="hipro-consent"
          />
        }
        label={
          <div className={classes.labelWrapper}>
            <span className={classes.requiredFielAsterisk}>
              {InstallmentsEnum.Asterisk}
            </span>
            {hiProConsent.label}
          </div>
        }
        className={classnames(classes.consent, 'focusVisibleOutline', {
          [classes.consentError]: hiProConsentError,
        })}
        onKeyDown={(e) => {
          if (
            e.target === e.currentTarget &&
            (e.key === 'Enter' || e.key === ' ')
          ) {
            handleHiproConsentClick();
          }
        }}
      />
      <div id="hipro-btn-start-process">
        <NewButton
          className={classes.submitButton}
          fullWidth
          title={InstallmentsEnum.Begin}
          variant="contained"
          color="primary"
          onClick={handleActionButtonClick}
          disabled={!hiProConsentChecked}
          isLoading={isLoading}
          data-cy="hipro-consent-btn"
          rounded={isFormMicroCenterActive}
        />
      </div>
      <div className={classes.footerSection}>
        <p className={classes.footerText}>
          {InstallmentsEnum.InstallmentsAreSupportedBy}
        </p>
        <Image
          src={'./images/mubi-installments/wonga.png'}
          srcSet={'./images/mubi-installments/wonga@2x.png 2x'}
          srcSetWebp={
            './images/mubi-installments/wonga.webp, ./images/mubi-installments/wonga@2x.webp 2x'
          }
          alt={'cars'}
          className={classes.img}
        />
      </div>
    </div>
  );
};

export default ApplicationInfo;
